import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandName from "@/components/BrandName";
import FeaturedUnionsStrip from "@/components/FeaturedUnionsStrip";
import api from "@/lib/api";
import {
  ShieldCheck, BadgeCheck, Users, Lightbulb, Camera, Wrench,
  Clapperboard, Mic, Headphones, Film, Palette, UserCog
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const DEPTS = [
  { icon: Lightbulb,   label: "LIGHTING" },
  { icon: Camera,      label: "CAMERA" },
  { icon: Wrench,      label: "GRIP" },
  { icon: Clapperboard,label: "DIRECTION" },
  { icon: Palette,     label: "MAKEUP" },
  { icon: Mic,         label: "SOUND" },
  { icon: Film,        label: "PRODUCTION" },
  { icon: UserCog,     label: "ART DEPT." },
  { icon: Users,       label: "SPOT BOY" },
];

const HERO_BG = "https://customer-assets.emergentagent.com/job_b9d46eef-a67c-4337-bd3b-2ebb181b20f0/artifacts/ya6u9urc_Call%20Sheet%20Homepage.jpeg";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [unionCount, setUnionCount] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/public/associations", { params: { limit: 1 } })
      .then(({ data }) => alive && setUnionCount(data.total))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen text-white" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        {/* Full hero image — untouched, spans the entire section. No masks,
            no horizontal fades, no cropping. The image itself is the design;
            we only tone it down uniformly with opacity so the CSS type
            sits legibly on top. */}
        <div
          className="absolute inset-0 opacity-[0.6]"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
          data-testid="hero-bg-image"
        />
        {/* Grain — global film-still texture. */}
        <div className="absolute inset-0 cs-grain" />
        {/* Text scrim — DESKTOP.
            Wide radial dark plate anchored to the left half of the hero
            (roughly 30 % x, 55 % y). Covers the entire text column
            (headline → CTAs) and leaves the right ~35 % of the hero
            untouched so the crew-silhouette section stays fully visible.
            Hidden on mobile — mobile uses a vertical band instead. */}
        <div
          className="absolute inset-0 pointer-events-none hidden md:block"
          style={{
            background:
              "radial-gradient(ellipse 78% 95% at 30% 55%, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.82) 30%, rgba(5,5,5,0.55) 55%, rgba(5,5,5,0.2) 78%, transparent 92%)",
          }}
          data-testid="hero-text-scrim-desktop"
        />
        {/* Text scrim — MOBILE.
            On phones the text column spans the full width so a left-anchored
            ellipse won't cover the right edge of the copy. Instead we use a
            top-anchored vertical band that darkens the whole width where the
            text sits, then fades to transparent below so the crew silhouettes
            stay visible at the bottom of the hero. */}
        <div
          className="absolute inset-0 pointer-events-none md:hidden"
          style={{
            background:
              "linear-gradient(to bottom, rgba(5,5,5,0.9) 0%, rgba(5,5,5,0.85) 40%, rgba(5,5,5,0.6) 65%, rgba(5,5,5,0.25) 85%, transparent 100%)",
          }}
          data-testid="hero-text-scrim-mobile"
        />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-32">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#F5B82E]" />
            <span className="text-[11px] tracking-[0.35em] uppercase text-[#F5B82E]">
              Brand Book · 2026
            </span>
          </div>

          <BrandName variant="gold" size="hero" withClapper />

          <h2 className="mt-6 max-w-2xl text-base sm:text-lg font-display tracking-[0.18em] uppercase text-[#F5B82E]">
            Digital Identity of Entire Media and Film Spectrum
          </h2>

          <p className="mt-6 max-w-xl text-white/70 text-base leading-relaxed">
            One verified identity. One platform. From the first call time to wrap —
            CALL SHEET connects unions, production houses, vendors, and crews
            across the film, TV and advertising industries.
          </p>

          {/* Trust strip */}
          <div className="mt-10 flex flex-wrap gap-6">
            <TrustPill icon={ShieldCheck} title="VERIFIED IDENTITY" />
            <TrustPill icon={BadgeCheck}  title="TRUSTED PROFESSIONALS" />
            <TrustPill icon={Users}       title="STRONGER INDUSTRY" />
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap gap-3">
            {user ? (
              <Button
                onClick={() => nav("/dashboard")}
                data-testid="cta-dashboard"
                className="btn-themed h-12 px-7 text-xs uppercase tracking-[0.25em] font-semibold rounded-full"
              >
                Enter Dashboard →
              </Button>
            ) : (
              <>
                <Link to="/associations">
                  <Button
                    data-testid="cta-browse-unions"
                    className="btn-themed h-12 px-7 text-xs uppercase tracking-[0.25em] font-semibold rounded-full"
                  >
                    Browse the Union Registry →
                  </Button>
                </Link>
                <Link to="/onboard">
                  <Button
                    data-testid="cta-onboard"
                    variant="outline"
                    className="h-12 px-7 text-xs uppercase tracking-[0.25em] font-semibold rounded-full border-white/30 text-white hover:bg-white hover:text-black bg-transparent"
                  >
                    Onboard your business
                  </Button>
                </Link>
                <Link to="/login">
                  <Button
                    data-testid="cta-login"
                    variant="outline"
                    className="h-12 px-7 text-xs uppercase tracking-[0.25em] font-semibold rounded-full border-white/30 text-white hover:bg-white hover:text-black bg-transparent"
                  >
                    Login
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Live count nudge — flags the registry as the trigger point */}
          {!user && (
            <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/55"
                 data-testid="hero-registry-nudge">
              <ShieldCheck size={12} className="text-[#F5B82E]" />
              <span className="text-white/85 font-semibold">
                {unionCount ?? "220"}+ verified unions
              </span>
              <span className="text-white/35">·</span>
              <span>Claim your digital card in 60 seconds</span>
              <Link to="/associations"
                className="ml-1 text-[#F5B82E] hover:text-[#F5D95E] underline underline-offset-4"
                data-testid="hero-registry-link">
                See if yours is listed →
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* DEPARTMENT STRIP — mirrors Facebook cover reference */}
      <section className="border-b border-white/10 bg-[#0a0a0a] cs-striped">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 py-10">
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-10 gap-y-8 gap-x-4">
            {DEPTS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center text-center group">
                <Icon size={28} className="text-[#F5B82E] group-hover:scale-110 transition-transform" />
                <span className="mt-2 text-[10px] tracking-[0.2em] uppercase text-white/70 font-display">
                  {label}
                </span>
              </div>
            ))}
            <div className="flex flex-col items-center justify-center text-[10px] tracking-[0.2em] uppercase text-[#F5B82E] font-display col-span-3 sm:col-span-1">
              & MANY MORE
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED UNIONS — public acquisition hook. Auto-geo-tagged
          via /api/geo/detect; falls back to most-claimed. */}
      {!user && <FeaturedUnionsStrip />}

      {/* WORKSPACE POSITIONING */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-20">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#F5B82E] mb-3">Workspace Optimization</div>
          <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-tight whitespace-nowrap">Tailored to Your Role. Built for the Set.</h3>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-10">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 flex flex-wrap items-center justify-between gap-4">
          <BrandName variant="silver" size="sm" withClapper />
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40">
            © 2026 · Powered by CALLSHEET
          </div>
        </div>
      </footer>
    </div>
  );
}

function TrustPill({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 border border-white/10">
      <Icon size={16} className="text-[#F5B82E]" />
      <span className="text-[10px] tracking-[0.25em] uppercase text-white/80 font-display">{title}</span>
    </div>
  );
}
